import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface PropertyMapProps {
  address: string;
  city: string;
  state: string;
  zipCode?: string;
  propertyName: string;
  className?: string;
}

interface GeocodeResult {
  lat: number;
  lng: number;
}

const PropertyMap: React.FC<PropertyMapProps> = ({
  address,
  city,
  state,
  zipCode,
  propertyName,
  className = ''
}) => {
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fullAddress = `${address}, ${city}, ${state}${zipCode ? ` ${zipCode}` : ''}`;

  // Google Maps Geocoding function
  const geocodeAddress = async (address: string): Promise<GeocodeResult | null> => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return null;

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return {
          lat: location.lat,
          lng: location.lng
        };
      } else {
        console.warn('Geocoding failed:', data.status, data.error_message);
        return null;
      }
    } catch (error) {
      console.error('Geocoding request failed:', error);
      return null;
    }
  };

  // Fallback coordinates for major cities
  const getFallbackCoordinates = (city: string, state: string): [number, number] => {
    const fallbacks: { [key: string]: [number, number] } = {
      'Tampa, FL': [27.9506, -82.4572],
      'Clearwater, FL': [27.9659, -82.8001],
      'Seminole, FL': [27.8397, -82.7912],
      'Apollo Beach, FL': [27.7731, -82.4076],
      'Kissimmee, FL': [28.2919, -81.4076],
      'Phoenix, AZ': [33.4484, -112.0740],
      'Scottsdale, AZ': [33.4942, -111.9261],
      'Tempe, AZ': [33.4255, -111.9400],
      'Mesa, AZ': [33.4152, -111.8315],
      'Chandler, AZ': [33.3062, -111.8413],
      'Los Angeles, CA': [34.0522, -118.2437],
      'San Francisco, CA': [37.7749, -122.4194],
      'San Diego, CA': [32.7157, -117.1611],
      'Sacramento, CA': [38.5816, -121.4944],
      'San Jose, CA': [37.3382, -121.8863],
      'Austin, TX': [30.2672, -97.7431],
      'Houston, TX': [29.7604, -95.3698],
      'Dallas, TX': [32.7767, -96.7970],
      'San Antonio, TX': [29.4241, -98.4936],
      'Fort Worth, TX': [32.7555, -97.3308],
      'Miami, FL': [25.7617, -80.1918],
      'Orlando, FL': [28.5383, -81.3792],
      'Jacksonville, FL': [30.3322, -81.6557],
      'Tallahassee, FL': [30.4518, -84.2807],
      'St. Petersburg, FL': [27.7663, -82.6404],
    };

    const key = `${city}, ${state}`;
    return fallbacks[key] || [39.8283, -98.5795]; // Default to center of US
  };

  useEffect(() => {
    const loadCoordinates = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try Google Maps geocoding first
        const geocodedCoords = await geocodeAddress(fullAddress);

        if (geocodedCoords) {
          setCoordinates([geocodedCoords.lat, geocodedCoords.lng]);
        } else {
          // Fallback to predefined coordinates
          console.log('Using fallback coordinates for:', fullAddress);
          setCoordinates(getFallbackCoordinates(city, state));
        }
      } catch (error) {
        console.error('Error loading coordinates:', error);
        setError('Failed to load map location');
        // Use fallback coordinates on error
        setCoordinates(getFallbackCoordinates(city, state));
      } finally {
        setLoading(false);
      }
    };

    loadCoordinates();
  }, [address, city, state, zipCode, fullAddress]);

  if (loading) {
    return (
      <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}>
        <div className="h-96 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Loading map...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !coordinates) {
    return (
      <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}>
        <div className="h-96 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-red-500 mb-2">⚠️</div>
            <p className="text-sm text-gray-600">
              {error || 'Unable to load map location'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {fullAddress}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}>
      <MapContainer
        center={coordinates}
        zoom={15}
        style={{ height: '300px', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={coordinates}>
          <Popup>
            <div className="text-center">
              <div className="font-semibold text-gray-900">{propertyName}</div>
              <div className="text-sm text-gray-600 mt-1">{fullAddress}</div>
              <div className="text-xs text-gray-500 mt-1">
                Lat: {coordinates[0].toFixed(4)}, Lng: {coordinates[1].toFixed(4)}
              </div>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

export default PropertyMap;